import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { contactsService } from '../services/contacts.service';
import { authService } from '../services/auth.service';
import {
  ContactRequest,
  ColocAgency,
  ContactAction,
  CONTACT_STATUS_LABELS,
  CONTACT_ACTION_LABELS,
  CONTACT_ACTION_OPTIONS,
  PROFESSIONAL_STATUS_LABELS,
  STAY_DURATION_LABELS,
} from '../types/contact';
import { getErrorMessage } from '../utils/error';
import { formatDate } from '../utils/format';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';

/**
 * Une demande de contact — une PAGE, plus un panneau latéral.
 *
 * Ce détail porte trois formulaires (édition, assignation, journal d'action) et
 * un historique. Le panneau de 460 px les empilait en colonne unique et masquait
 * la moitié des colonnes de la liste pour tenir à l'écran.
 */
const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();

  const [contact, setContact] = useState<ContactRequest | null>(null);
  const [agencies, setAgencies] = useState<ColocAgency[]>([]);
  const [introuvable, setIntrouvable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [assignAgencyId, setAssignAgencyId] = useState('');
  const [assignBody, setAssignBody] = useState('');
  const [relanceMessage, setRelanceMessage] = useState('');

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  const [actions, setActions] = useState<ContactAction[]>([]);
  const [actionsLoading, setActionsLoading] = useState(true);
  const [actionType, setActionType] = useState('CALLED');
  const [actionNote, setActionNote] = useState('');

  // Corps par défaut de l'email d'assignation (éditable par l'owner).
  const defaultAssignBody = useCallback((c: ContactRequest): string => {
    const u = authService.getCurrentUser()?.user;
    const ownerName = `${u?.firstName || ''} ${u?.lastName || ''}`.trim();
    const first = (c.name || '').trim().split(/\s+/)[0] || '';
    const coloc = selectedColoc?.name || '';
    return [
      'Bonjour,',
      '',
      `Je me permets de vous mettre en relation avec ${first} (en copie de cet email),`,
      `à la recherche d'une chambre dans la colocation ${coloc} et qui aimerait échanger avec vous.`,
      `Téléphone : ${c.phone || '—'}`,
      '',
      `${first}, l'agence va revenir vers vous très rapidement.`,
      '',
      'Bien cordialement,',
      ownerName,
    ].join('\n');
  }, [selectedColoc]);

  const loadData = useCallback(async () => {
    // Pas encore de colocation sélectionnée : on RESTE en chargement. Sur un
    // lien direct, `Layout` la pose juste après le montage et l'évènement
    // `colocChanged` relance ce chargement — rendre « introuvable » entre les
    // deux ferait clignoter un message faux.
    if (!selectedColoc?.id || !id) return;
    setLoading(true);
    try {
      // Pas d'endpoint « un contact » : la liste de la coloc est déjà bornée
      // par le backend, et le menu d'assignation a besoin des agences.
      const [data, ags] = await Promise.all([
        contactsService.listForColoc(selectedColoc.id),
        contactsService.listColocAgencies(selectedColoc.id),
      ]);
      const trouve = data.find((c) => c.id === id) ?? null;
      setContact(trouve);
      setIntrouvable(trouve === null);
      setAgencies(ags);
      // Pré-remplit le corps éditable tant que la demande n'est ni assignée ni
      // clôturée — ne pas l'écraser si le propriétaire l'a déjà retouché.
      setAssignBody((precedent) =>
        precedent
        || (trouve && !trouve.assignedAgencyUserId && trouve.status !== 'CLOSED'
          ? defaultAssignBody(trouve)
          : ''),
      );
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement du contact'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc, id, defaultAssignBody]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadActions = useCallback(async () => {
    if (!id) return;
    setActionsLoading(true);
    try {
      setActions(await contactsService.getActions(id));
    } catch {
      setActions([]);
    } finally {
      setActionsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  // Ticket #20 : « Email envoyé » s'affichait aussi quand l'agence n'avait rien reçu.
  const emailNotice = (result: unknown): string => {
    const email = (result as { email?: { dry_run?: boolean; sent?: boolean; redirected?: boolean; to?: string } })?.email;
    if (email?.dry_run) return "Email en mode test : non envoyé (le flag d'envoi est désactivé).";
    if (email && !email.sent) return "L'email n'a pas pu être envoyé.";
    if (email?.redirected) return `Mode test : l'email est parti vers ${email.to}, pas vers l'agence.`;
    return 'Email envoyé.';
  };

  const handleLogAction = async () => {
    if (!id) return;
    setBusy(true);
    setNotice(null);
    try {
      await contactsService.logOwnerAction(id, actionType, actionNote);
      setActionNote('');
      setNotice('Action enregistrée.');
      await Promise.all([loadActions(), loadData()]);
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'enregistrement de l'action"));
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async () => {
    if (!id || !assignAgencyId) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await contactsService.assign(id, assignAgencyId, assignBody);
      setNotice(`Contact assigné. ${emailNotice(res)}`);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'assignation"));
    } finally {
      setBusy(false);
    }
  };

  const handleRelance = async () => {
    if (!id) return;
    setBusy(true);
    setNotice(null);
    try {
      const res = await contactsService.relance(id, relanceMessage);
      setRelanceMessage('');
      setNotice(`Relance effectuée. ${emailNotice(res)}`);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la relance'));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    if (!contact) return;
    setEditName(contact.name || '');
    setEditEmail(contact.email || '');
    setEditPhone(contact.phone || '');
    setEditMessage(contact.message || '');
    setNotice(null);
    setEditing(true);
  };

  const handleUpdate = async () => {
    if (!id || !editName.trim() || !editEmail.trim()) return;
    setEditBusy(true);
    setNotice(null);
    try {
      await contactsService.updateContact(id, {
        name: editName.trim(),
        email: editEmail.trim(),
        phone: editPhone.trim(),
        message: editMessage.trim(),
      });
      setEditing(false);
      setNotice('Contact modifié.');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la modification du contact'));
    } finally {
      setEditBusy(false);
    }
  };

  // Après une suppression, la demande n'existe plus : rester sur sa page
  // afficherait « Demande introuvable ». On rend la main à la liste.
  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm(
      'Supprimer définitivement ce contact et son historique ? Cette action est irréversible.',
    )) return;
    setBusy(true);
    setNotice(null);
    try {
      await contactsService.deleteContact(id);
      navigate('/contacts');
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la suppression du contact'));
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (!id) return;
    setBusy(true);
    setNotice(null);
    try {
      await contactsService.close(id);
      setNotice('Contact clôturé.');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la clôture'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  if (introuvable || !contact) {
    return (
      <div className="candidats-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">Demande introuvable</p>
          <p className="socle-vide-aide">
            Elle a été supprimée, ou elle appartient à une autre colocation que
            celle sélectionnée.
          </p>
          <button className="btn-action btn-secondary" onClick={() => navigate('/contacts')}>
            Retour aux demandes
          </button>
        </div>
      </div>
    );
  }

  const isAssigned = !!contact.assignedAgencyUserId;
  const isClosed = contact.status === 'CLOSED';
  // Une agence suspendue reste listée (pour la rétablir), mais ne reçoit rien :
  // ni assignation, ni relance (ticket #20).
  const agencesActives = agencies.filter((a) => !a.suspended);
  const agenceAssigneeSuspendue = agencies.some(
    (a) => a.id === contact.assignedAgencyUserId && a.suspended,
  );

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate('/contacts')}
          >
            ← Demandes de contact
          </button>
          <h2>{contact.name}</h2>
          <p className="access-hint" style={{ margin: 0 }}>
            {CONTACT_STATUS_LABELS[contact.status] || contact.status}
            {contact.assignedAgencyName ? ` — ${contact.assignedAgencyName}` : ''}
          </p>
        </div>
      </div>

      {error && <div className="candidats-error">{error}</div>}
      {notice && <div className="candidats-notice">{notice}</div>}

      <div className="cx-detail">
        <div className="cx-card">
          <h4 className="cx-title">Demande</h4>
          {editing ? (
            <div className="action-form-grid">
              <div className="action-field">
                <label htmlFor="ed-name">Nom *</label>
                <input id="ed-name" type="text" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="ed-email">Email *</label>
                <input id="ed-email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="ed-phone">Téléphone</label>
                <input id="ed-phone" type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
              </div>
              <div className="action-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="ed-msg">Message</label>
                <textarea id="ed-msg" rows={3} value={editMessage} onChange={(e) => setEditMessage(e.target.value)} />
              </div>
              <div className="action-actions">
                <button className="btn-action btn-save" disabled={editBusy || !editName.trim() || !editEmail.trim()} onClick={handleUpdate}>
                  Enregistrer
                </button>
                <button className="btn-action btn-secondary" disabled={editBusy} onClick={() => setEditing(false)}>
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="cx-row">
                <span className="cx-label">Email</span>
                <span className="cx-value">{contact.email}</span>
              </div>
              <div className="cx-row">
                <span className="cx-label">Téléphone</span>
                <span className="cx-value">{contact.phone || '—'}</span>
              </div>
              <div className="cx-row">
                <span className="cx-label">Reçu le</span>
                <span className="cx-value">{contact.dateCreated ? formatDate(contact.dateCreated) : '—'}</span>
              </div>
              {contact.roomName && (
                <div className="cx-row">
                  <span className="cx-label">Chambre</span>
                  <span className="cx-value">{contact.roomName}</span>
                </div>
              )}
              {contact.professionalStatus && (
                <div className="cx-row">
                  <span className="cx-label">Situation</span>
                  <span className="cx-value">
                    {PROFESSIONAL_STATUS_LABELS[contact.professionalStatus] || contact.professionalStatus}
                  </span>
                </div>
              )}
              {contact.moveInDate && (
                <div className="cx-row">
                  <span className="cx-label">Emménagement</span>
                  <span className="cx-value">{formatDate(contact.moveInDate)}</span>
                </div>
              )}
              {contact.stayDuration && (
                <div className="cx-row">
                  <span className="cx-label">Durée envisagée</span>
                  <span className="cx-value">
                    {STAY_DURATION_LABELS[contact.stayDuration] || contact.stayDuration}
                  </span>
                </div>
              )}
              {contact.message && <p className="cx-message">{contact.message}</p>}
              {/* `cx-actions` plutôt qu'un style en ligne : socle.css vise toute
                  classe contenant « actions » et pose la disposition ET l'écart
                  supplémentaire avant le bouton destructeur. */}
              <div className="cx-actions" style={{ marginTop: 14 }}>
                <button className="btn-action btn-secondary" onClick={startEdit}>
                  Modifier
                </button>
                {/* Clôturer porte sur LA DEMANDE, pas sur l'agence : sa place est
                    ici, avec Modifier et Supprimer. */}
                {!isClosed && (
                  <button className="btn-action btn-secondary" disabled={busy} onClick={handleClose}>
                    Clôturer sans suite
                  </button>
                )}
                <button className="btn-action btn-danger" disabled={busy} onClick={handleDelete}>
                  Supprimer
                </button>
              </div>
            </>
          )}
        </div>

        <div className="cx-card">
          <h4 className="cx-title">Suivi</h4>
          {isClosed ? (
            <p className="timeline-empty">
              Clôturé{contact.assignedAgencyName ? ` — agence : ${contact.assignedAgencyName}` : ''}.
            </p>
          ) : isAssigned ? (
            <div>
              <div className="cx-row">
                <span className="cx-label">Agence assignée</span>
                <span className="cx-value">{contact.assignedAgencyName}</span>
              </div>
              {contact.ownerContextMessage && (
                <div style={{ marginTop: 8 }}>
                  <span className="cx-label">Message de contexte</span>
                  <p className="cx-context">{contact.ownerContextMessage}</p>
                </div>
              )}
              <div className="action-form-grid" style={{ marginTop: 16 }}>
                <div className="action-field" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="relance">Message de relance (optionnel)</label>
                  <textarea
                    id="relance"
                    rows={3}
                    value={relanceMessage}
                    onChange={(e) => setRelanceMessage(e.target.value)}
                    placeholder="Ajouter un mot pour l'agence…"
                  />
                </div>
                <div className="action-actions">
                  {agenceAssigneeSuspendue ? (
                    <p className="timeline-empty">
                      L'accès de cette agence est suspendu : rétablissez-le pour la relancer.
                    </p>
                  ) : (
                    <button className="btn-action btn-save" disabled={busy} onClick={handleRelance}>
                      Relancer l'agence
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : agencesActives.length === 0 ? (
            <p className="timeline-empty">
              Aucune agence n'a d'accès actif à cette colocation. Donnez d'abord accès à une agence pour pouvoir lui assigner un contact.
            </p>
          ) : (
            <div className="action-form-grid">
              <div className="action-field">
                <label htmlFor="agency">Agence</label>
                <select
                  id="agency"
                  value={assignAgencyId}
                  onChange={(e) => setAssignAgencyId(e.target.value)}
                >
                  <option value="">— Choisir une agence —</option>
                  {agencesActives.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="action-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="ctx">Message envoyé à l'agence (modifiable)</label>
                <textarea
                  id="ctx"
                  rows={8}
                  value={assignBody}
                  onChange={(e) => setAssignBody(e.target.value)}
                  placeholder="Corps de l'email envoyé à l'agence…"
                />
              </div>
              <div className="action-actions">
                <button className="btn-action btn-save" disabled={busy || !assignAgencyId} onClick={handleAssign}>
                  Assigner à l'agence
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Journal de suivi — accessible au propriétaire qu'il travaille seul
            ou via une agence. */}
        <div className="cx-card">
          <h4 className="cx-title">Historique</h4>
          {!isClosed && (
            <div className="action-form-grid" style={{ marginBottom: 16 }}>
              <div className="action-field">
                <label htmlFor="action-type">Type d&apos;action</label>
                <select
                  id="action-type"
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                >
                  {CONTACT_ACTION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="action-field">
                <label htmlFor="action-note">Note</label>
                <textarea
                  id="action-note"
                  rows={3}
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  placeholder="Détails de l'échange, prochaines étapes…"
                />
              </div>
              <div className="action-actions">
                <button className="btn-action btn-save" disabled={busy} onClick={handleLogAction}>
                  Enregistrer l&apos;action
                </button>
              </div>
            </div>
          )}
          {actionsLoading ? (
            <p className="timeline-empty">Chargement de l&apos;historique…</p>
          ) : actions.length === 0 ? (
            <p className="timeline-empty">Aucune action pour l&apos;instant.</p>
          ) : (
            <ul className="action-timeline">
              {actions.map((a) => (
                <li key={a.id}>
                  <strong>{CONTACT_ACTION_LABELS[a.actionType] || a.actionType}</strong>
                  {a.createdAt ? ` · ${formatDate(a.createdAt)}` : ''}
                  {a.agencyName ? ` · ${a.agencyName}` : ''}
                  {a.note && <p className="cx-context">{a.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactDetailPage;
